import React, { useState } from "react";
import { Box, Typography, Chip, Collapse } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { SECTIONS } from "../constants/sections";
import { PREVIEW_MAP } from "../previews";
import { isSectionFilled } from "../utils/sectionUtils";

const OVERVIEW_SECTIONS = [
  "team",
  "features",
  "pricing",
  "testimonials",
  "faq",
  "caseStudies",
];

const getItemCount = (key, form) => {
  if (key === "pricing") return form[key]?.packages?.length ?? 0;
  return form[key]?.items?.length ?? 0;
};

const SectionCanvas = ({
  form,
  setForm,
  effectiveId, // ← add karo
  saveToBackend, // ← add karo
  onSectionClick,
  onAddNew,
  openCaseStudyModal,
  setOverviewSection,
  setDetailSection,
  setDetailIndex,
  setDetailDraft,
}) => {
  // Track which sections are collapsed. Default: all expanded.
  const [collapsed, setCollapsed] = useState({});

  const toggleCollapse = (key) =>
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

   const handleSectionFieldChange = (sectionKey, updatedData) => {
    setForm((prev) => {
      const next = { ...prev, [sectionKey]: updatedData };
      setTimeout(() => {
        if (effectiveId) saveToBackend(next);
      }, 800);
      return next;
    });
  };

  return (
    <Box flex={1} sx={{ overflowY: "auto", p: 3 }}>
      <Box display="flex" flexDirection="column" gap={1.5}>
        {SECTIONS.map((s) => {
          const filled = isSectionFilled(s.key, form);
          const PreviewComponent = PREVIEW_MAP[s.key];
          const isOverview = OVERVIEW_SECTIONS.includes(s.key);
          const itemCount = isOverview ? getItemCount(s.key, form) : null;
          const isCollapsed = !!collapsed[s.key];

          

          const handleEdit = (sectionKey, index) => {
            if (sectionKey === "caseStudies") {
              openCaseStudyModal(index, form.caseStudies.items[index]);
              return;
            }
            setOverviewSection(null);
            setDetailSection(sectionKey);
            setDetailIndex(index);
            const arr =
              sectionKey === "pricing"
                ? form[sectionKey].packages
                : form[sectionKey].items;
            setDetailDraft({ ...arr[index] });
          };

          const handleDelete = (sectionKey, index) => {
            const arr =
              sectionKey === "pricing"
                ? form[sectionKey].packages
                : form[sectionKey].items;
            const updated = arr.filter((_, i) => i !== index);
            setForm((prev) => ({
              ...prev,
              [sectionKey]: {
                ...prev[sectionKey],
                [sectionKey === "pricing" ? "packages" : "items"]: updated,
              },
            }));
          };

          return (
            <Box
              key={s.key}
              sx={{
                border: "1px solid rgba(255,255,255,0.07)",
                borderRadius: "12px",
                overflow: "hidden",
                transition: "border-color 0.2s",
                background: filled ? "rgba(255,255,255,0.02)" : "transparent",
                "&:hover": { borderColor: "rgba(59,130,246,0.4)" },
              }}
            >
              {/* ── Section header bar ── */}
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  px: 2,
                  py: 1.25,
                  borderBottom: filled
                    ? "1px solid rgba(255,255,255,0.06)"
                    : "none",
                  cursor: "pointer",
                }}
                onClick={() => {
                  if (filled) {
                    // Filled sections: header toggles collapse
                    toggleCollapse(s.key);
                  } else {
                    // Empty sections: header opens modal
                    onSectionClick(s.key);
                  }
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography fontSize={13}>{s.icon}</Typography>
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    sx={{
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      color: "text.secondary",
                    }}
                  >
                    {s.label}
                  </Typography>

                  {isOverview && itemCount !== null && (
                    <Box
                      component="span"
                      sx={{
                        ml: 0.5,
                        fontSize: "10px",
                        fontWeight: 500,
                        color: "text.disabled",
                      }}
                    >
                      · {itemCount} {itemCount === 1 ? "item" : "items"}
                    </Box>
                  )}

                  {s.required && !filled && (
                    <Chip
                      label="Required"
                      size="small"
                      sx={{
                        fontSize: "9px",
                        height: 16,
                        background: "rgba(244,67,54,0.15)",
                        color: "#f44336",
                      }}
                    />
                  )}
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  {/* Edit button — only for filled non-overview sections */}
                  {filled && !isOverview && (
                    <Box
                      component="span"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSectionClick(s.key);
                      }}
                      sx={{
                        fontSize: "11px",
                        px: 1,
                        py: 0.4,
                        borderRadius: "6px",
                        border: "1px solid rgba(255,255,255,0.1)",
                        color: "text.secondary",
                        "&:hover": {
                          borderColor: "#3b82f6",
                          color: "#3b82f6",
                        },
                      }}
                    >
                      Edit
                    </Box>
                  )}

                  {/* Add button — only for empty sections */}
                  {!filled && (
                    <Box
                      component="span"
                      sx={{
                        fontSize: "11px",
                        px: 1,
                        py: 0.4,
                        borderRadius: "6px",
                        border: "1px solid rgba(255,255,255,0.1)",
                        color: "text.secondary",
                        "&:hover": {
                          borderColor: "#3b82f6",
                          color: "#3b82f6",
                        },
                      }}
                    >
                      + Add
                    </Box>
                  )}

                  {/* Chevron — only for filled sections */}
                  {filled && (
                    <KeyboardArrowDownIcon
                      sx={{
                        fontSize: 16,
                        color: "text.disabled",
                        transition: "transform 0.2s",
                        transform: isCollapsed
                          ? "rotate(-90deg)"
                          : "rotate(0deg)",
                      }}
                    />
                  )}
                </Box>
              </Box>

              {/* ── Section body (animated collapse) ── */}
              {filled && (
                <Collapse in={!isCollapsed} timeout={300} unmountOnExit>
                  <Box px={2.5} py={2}>
                    <PreviewComponent
                      data={form[s.key]}
                      onEdit={isOverview ? handleEdit : undefined}
                      onDelete={isOverview ? handleDelete : undefined}
                      onChange={(updated) =>
                        handleSectionFieldChange(s.key, updated)
                      }
                    />

                    {isOverview && (
                      <Box
                        onClick={(e) => onAddNew(s.key, e)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.75,
                          mt: 2,
                          py: 1.1,
                          borderRadius: "8px",
                          border: "1.5px dashed rgba(255,255,255,0.1)",
                          cursor: "pointer",
                          transition: "all 0.18s",
                          "&:hover": {
                            border: "1.5px dashed #3b82f6",
                            background: "rgba(59,130,246,0.04)",
                            "& .add-text": { color: "#3b82f6" },
                            "& .add-icon-inline": { color: "#3b82f6" },
                          },
                        }}
                      >
                        <AddIcon
                          className="add-icon-inline"
                          sx={{
                            fontSize: 14,
                            color: "rgba(255,255,255,0.25)",
                            transition: "color 0.18s",
                          }}
                        />
                        <Typography
                          className="add-text"
                          variant="caption"
                          fontWeight={500}
                          sx={{
                            color: "rgba(255,255,255,0.3)",
                            transition: "color 0.18s",
                          }}
                        >
                          Add {s.label.replace(/s$/, "")}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Collapse>
              )}

              {/* ── Empty state ── */}
              {!filled && (
                <Box
                  onClick={() => onSectionClick(s.key)}
                  sx={{
                    px: 2.5,
                    py: 2.5,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <AddIcon sx={{ fontSize: 14, color: "text.disabled" }} />
                  <Typography variant="caption" color="text.disabled">
                    Click to add {s.label.toLowerCase()} content
                  </Typography>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default SectionCanvas;
